import { createContext, useCallback, useEffect, useState } from 'react'
import {
  THEME_STORAGE_KEY,
  ThemePreferences,
  Themes,
  ThemeSources,
} from '../constants/theme.constants'

export const ThemeContext = createContext<UseThemeReturn>({} as UseThemeReturn)

/** The theme actually painted. */
export type Theme = 'light' | 'dark'

/** What gets stored: a choice, which may be the choice to defer to the OS/browser. */
export type ThemePreference = Theme | 'system'

export type ThemeSource = 'user' | 'system'

export interface UseThemeReturn {
  theme: Theme
  source: ThemeSource
  setTheme: (theme?: Theme) => void
}

const THEME_PREFERENCE_VALUES: ThemePreference[] = [
  ThemePreferences.LIGHT,
  ThemePreferences.DARK,
  ThemePreferences.SYSTEM,
]

/**
 * localStorage holds the *preference*, not the painted theme, and 'system' is written out
 * rather than represented by an absent key. The two have to be tellable apart: an absent key
 * used to mean both "never chose" and "chose system", so nothing on load could safely clear a
 * stale pick - which is how a long-forgotten 'dark' went on overriding the browser's own
 * appearance setting. A missing or unrecognised value reads as 'system', which keeps the
 * bare 'light'/'dark' written by older builds working.
 */
const readPreference = (): ThemePreference => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  return (
    THEME_PREFERENCE_VALUES.find((preference) => preference === stored) ?? ThemePreferences.SYSTEM
  )
}

const resolveTheme = (preference: ThemePreference): Theme => {
  if (preference !== ThemePreferences.SYSTEM) return preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? Themes.DARK : Themes.LIGHT
}

export const useTheme = (): UseThemeReturn => {
  const [theme, setThemeState] = useState<Theme>(Themes.LIGHT)
  const [source, setSource] = useState<ThemeSource>(ThemeSources.SYSTEM)

  /**
   * The one place that puts the stored preference on the document. Both pieces of state are
   * derived from storage here rather than tracked alongside it, so what the Settings picker
   * reports cannot drift from what is painted. Mirrored by the pre-paint guard in index.html,
   * which applies the same rule before React mounts.
   */
  const refreshTheme = useCallback(() => {
    const preference = readPreference()
    const resolved = resolveTheme(preference)

    document.documentElement.classList.toggle('dark', resolved === Themes.DARK)
    document.documentElement.setAttribute('data-theme', resolved)

    setThemeState(resolved)
    setSource(preference === ThemePreferences.SYSTEM ? ThemeSources.SYSTEM : ThemeSources.USER)
  }, [])

  useEffect(() => {
    refreshTheme()

    // Only has an effect while the preference is 'system', but staying subscribed regardless
    // costs nothing and means switching back to 'system' needs no re-subscription.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', refreshTheme)
    return () => mediaQuery.removeEventListener('change', refreshTheme)
  }, [refreshTheme])

  const setTheme = useCallback(
    (_theme?: Theme) => {
      // No argument means "follow the system", which is stored as 'system' rather than by
      // deleting the key - see readPreference.
      localStorage.setItem(THEME_STORAGE_KEY, _theme ?? ThemePreferences.SYSTEM)
      refreshTheme()
    },
    [refreshTheme],
  )

  return { theme, source, setTheme }
}
